import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  Vector4,
  HemisphericLight,
  MeshBuilder,
  StandardMaterial,
  MultiMaterial,
  SubMesh,
  Texture,
  Color3,
  PointerEventTypes,
  Animation,
  CubicEase,
  EasingFunction,
  Color4,
  Mesh
} from '@babylonjs/core';
import { Material } from '@babylonjs/core/Materials/material';
import type { Card } from './types';
import type { DeckManager } from './DeckManager';
import type { UIManager } from './UIManager';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';

interface CardMesh extends Mesh {
  cardData?: Card;
  cardBackData?: Card;
  originalPosition?: Vector3;
  originalScale?: Vector3;
  isHovered?: boolean;
}

export class CardGrid {
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private allCards: Card[] = [];
  private cards: Card[] = [];
  private cardMeshes: CardMesh[] = [];
  private filteredCards: Card[] = [];
  private deckManager: DeckManager;
  private uiManager: UIManager | null = null;

  private roundedMaskTexture: Texture | null = null;
  private searchQuery: string = '';
  private rarityFilter: string = 'all';

  constructor(canvas: HTMLCanvasElement, cards: Card[], deckManager: DeckManager) {
    this.allCards = cards;
    // IMPORTANT: clone card objects to avoid shared-reference bugs when a card is mutated
    // elsewhere (e.g. transform / modal / deck interactions).
    this.cards = cards.filter(c => !c.isBackFace).map(c => ({ ...c }));
    this.filteredCards = this.cards.map(c => ({ ...c }));
    this.deckManager = deckManager;

    // Create engine - simple WebGL mode for maximum compatibility
    this.engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      disableWebGL2Support: false
    });

    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.1, 0.1, 0.15, 1);

    // Camera
    this.camera = new ArcRotateCamera(
      'camera',
      Math.PI / 2,
      Math.PI / 2.4,
      25,
      Vector3.Zero(),
      this.scene
    );
    this.camera.attachControl(canvas, true);
    this.camera.lowerRadiusLimit = 3;
    this.camera.upperRadiusLimit = 50;
    // Allow full 'globe-like' orbit (look above/below/behind)
    this.camera.allowUpsideDown = true;
    this.camera.lowerBetaLimit = 0.01;
    this.camera.upperBetaLimit = Math.PI - 0.01;
    this.camera.wheelPrecision = 50;
    this.camera.panningSensibility = 100;

    // Lighting
    const light = new HemisphericLight('light', new Vector3(0, 1, 0), this.scene);
    light.intensity = 1.2;
    light.specular = new Color3(0.2, 0.2, 0.2);

    // Additional lights for better card visibility
    const light2 = new HemisphericLight('light2', new Vector3(0, -1, 0), this.scene);
    light2.intensity = 0.5;

    this.setupInteractions();
    this.createCardGrid();

    // Run render loop
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });

    // Handle resize
    window.addEventListener('resize', () => {
      this.engine.resize();
    });

    this.setupFilters();
  }

  setUIManager(uiManager: UIManager): void {
    this.uiManager = uiManager;
  }

  private createCardGrid(): void {
    // Clear existing meshes
    this.cardMeshes.forEach(mesh => mesh.dispose());
    this.cardMeshes = [];

    const cardsToShow = this.filteredCards;
    const isMobile = window.innerWidth <= 700;

    // Wall layout (grid on X/Y), centered
    const n = cardsToShow.length;
    if (n === 0) return;

    const aspect = window.innerWidth / Math.max(1, window.innerHeight);
    const idealCols = Math.ceil(Math.sqrt(n * (isMobile ? 0.8 : aspect)));
    const cols = Math.max(isMobile ? 3 : 4, Math.min(isMobile ? 4 : 9, idealCols));
    const rows = Math.ceil(n / cols);

    const cardW = 2.0;
    const cardH = 3.0;
    const gapX = isMobile ? 0.35 : 0.55;
    const gapY = isMobile ? 0.35 : 0.55;

    const spacingX = cardW + gapX;
    const spacingY = cardH + gapY;

    const wallWidth = (cols - 1) * spacingX;
    const wallHeight = (rows - 1) * spacingY;

    cardsToShow.forEach((card, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;

      const x = (col * spacingX) - wallWidth / 2;
      const y = (wallHeight / 2) - (row * spacingY);
      const z = 0;

      const cardMesh = this.createCardMesh(card, new Vector3(x, y, z));
      this.cardMeshes.push(cardMesh);
    });

    // Camera target: center of wall
    this.camera.setTarget(new Vector3(0, 0, 0));

    // Adjust camera radius to fit the wall nicely
    const fit = Math.max(wallWidth, wallHeight);
    const baseRadius = isMobile ? 14 : 16;
    this.camera.radius = Math.max(baseRadius, fit * 0.9 + 10);
  }



private getOrCreateRoundedMask(): Texture {
  if (this.roundedMaskTexture) return this.roundedMaskTexture;

  // Create a small alpha mask with rounded corners (shared by all cards)
  const size = 256;
  const dt = new DynamicTexture('roundedMask', { width: size, height: size }, this.scene, false);
  const ctx = dt.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, size, size);

  const r = 40; // corner radius in px
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(size - r, 0);
  ctx.quadraticCurveTo(size, 0, size, r);
  ctx.lineTo(size, size - r);
  ctx.quadraticCurveTo(size, size, size - r, size);
  ctx.lineTo(r, size);
  ctx.quadraticCurveTo(0, size, 0, size - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();

  dt.update(false);

  const tex = dt as unknown as Texture;
  // Ensure the mask orientation matches the diffuse textures
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;

  this.roundedMaskTexture = tex;
  return tex;
}

private applyRoundedMask(mat: StandardMaterial) {
  const mask = this.getOrCreateRoundedMask();
  mat.opacityTexture = mask;
  mat.useAlphaFromDiffuseTexture = false;
  mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
}
  private createCardMesh(card: Card, position: Vector3): CardMesh {
    // Create a thin box to give real thickness (3D card)
    const box = MeshBuilder.CreateBox(
      `card_${card.number}`,
      {
        width: 2,
        height: 3,
        depth: 0.12,
        // Fix back-face mirroring: flip U on the back face UVs
        faceUV: [
          new Vector4(1, 0, 0, 1), // front (flip U to fix horizontal mirror)
          new Vector4(0, 1, 1, 0), // back (flip only V to keep same orientation as front)
          new Vector4(0, 0, 1, 1), // right
          new Vector4(0, 0, 1, 1), // left
          new Vector4(0, 0, 1, 1), // top
          new Vector4(0, 0, 1, 1)  // bottom
        ]
      },
      this.scene
    ) as CardMesh;

    box.position = position;
    // Store an isolated copy to avoid accidental shared mutations
    box.cardData = { ...card };
    box.originalPosition = position.clone();
    box.originalScale = new Vector3(1, 1, 1);

    // Find back-face card data (double-faced cards)
    // - If no back is defined, we treat the back as identical to the front.
    let backCard: Card | undefined;
    if (card.transformTo != null) {
      backCard = this.allCards.find(c => c.number === card.transformTo) ||
                 this.allCards.find(c => c.isBackFace && c.transformFrom === card.number);
    } else {
      backCard = this.allCards.find(c => c.isBackFace && c.transformFrom === card.number);
    }
    // Store for click/modal logic
    box.cardBackData = backCard ? { ...backCard } : { ...card };

    const frontUrl = card.image ? card.image.replace(/^\.\//, '/') : '';
    const backUrl = (backCard?.image || card.image || '').replace(/^\.\//, '/');

    const makeTexturedMat = (name: string, url: string, fallback: Color3) => {
      const mat = new StandardMaterial(name, this.scene);
      mat.specularColor = new Color3(0.1, 0.1, 0.1);
      mat.emissiveColor = new Color3(0.18, 0.18, 0.18);
      if (url) {
        try {
          const tex = new Texture(url, this.scene, false, false);
          tex.updateSamplingMode(Texture.TRILINEAR_SAMPLINGMODE);
          tex.anisotropicFilteringLevel = 4;
          tex.hasAlpha = false;
          mat.diffuseTexture = tex;
          mat.emissiveTexture = tex;
        } catch {
          mat.diffuseColor = fallback;
        }
      } else {
        mat.diffuseColor = fallback;
      }
      this.applyRoundedMask(mat);
      return mat;
    };

    const edgeMat = new StandardMaterial(`edge_${card.number}`, this.scene);
    edgeMat.diffuseColor = new Color3(0.05, 0.05, 0.07);
    edgeMat.emissiveColor = new Color3(0.03, 0.03, 0.04);
    edgeMat.specularColor = new Color3(0.0, 0.0, 0.0);

    const frontFallback = card.rarity === 'R' ? new Color3(0.8, 0.6, 0.2) : new Color3(0.6, 0.7, 0.8);
    const frontMat = makeTexturedMat(`front_${card.number}`, frontUrl, frontFallback);
    const backMat = makeTexturedMat(`back_${card.number}`, backUrl, frontFallback);

    // MultiMaterial per face: 0 front, 1 back, 2 right, 3 left, 4 top, 5 bottom
    const multi = new MultiMaterial(`multi_${card.number}`, this.scene);
    multi.subMaterials = [frontMat, backMat, edgeMat, edgeMat, edgeMat, edgeMat];
    box.material = multi;

    // Split box indices into 6 faces (6 indices per face)
    box.subMeshes = [];
    const totalIndices = box.getTotalIndices();
    const totalVerts = box.getTotalVertices();
    const faceIndexCount = Math.floor(totalIndices / 6);  // should be 6
    const faceVertCount = Math.floor(totalVerts / 6);     // should be 4
    for (let face = 0; face < 6; face++) {
      new SubMesh(
        face,                 // materialIndex
        face * faceVertCount, // verticesStart
        faceVertCount,        // verticesCount
        face * faceIndexCount,// indexStart
        faceIndexCount,       // indexCount
        box
      );
    }

    // Slight tilt for depth/readability (small random)
    box.rotation.x = (Math.random() - 0.5) * 0.06;
    box.rotation.y = (Math.random() - 0.5) * 0.08;

    // Make cards a bit brighter in the scene
    box.renderingGroupId = 1;

    // Subtle floating animation (optional)
    this.addFloatingAnimation(box, 0.25 + (Math.random() * 0.15));

    return box;
  }

  private addFloatingAnimation(mesh: Mesh, delaySeconds: number = 0): void {
    const delay = delaySeconds;
    
    const anim = new Animation(
      'floating',
      'position.y',
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );

    const keys = [
      { frame: 0, value: mesh.position.y },
      { frame: 60, value: mesh.position.y + 0.1 },
      { frame: 120, value: mesh.position.y }
    ];

    anim.setKeys(keys);

    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    anim.setEasingFunction(ease);

    mesh.animations.push(anim);
    setTimeout(() => {
      this.scene.beginAnimation(mesh, 0, 120, true, 1);
    }, Math.max(0, delay) * 1000);
  }

  private setupInteractions(): void {
    let hoveredMesh: CardMesh | null = null;

    // Mobile detection - check once
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Double-activation guard (prevents accidental opens)
    // Mobile: double-tap, Desktop: double-click (slightly longer window)
    let lastTapTimeMobile = 0;
    let lastTapMeshMobile: CardMesh | null = null;
    const DOUBLE_TAP_DELAY_MOBILE = 300;

    let lastClickTimeDesktop = 0;
    let lastClickMeshDesktop: CardMesh | null = null;
    const DOUBLE_CLICK_DELAY_DESKTOP = 450;

    this.scene.onPointerObservable.add((pointerInfo) => {
      switch (pointerInfo.type) {
        case PointerEventTypes.POINTERMOVE:
          const pickResult = this.scene.pick(
            this.scene.pointerX,
            this.scene.pointerY
          );

          if (pickResult?.hit && pickResult.pickedMesh) {
            const mesh = pickResult.pickedMesh as CardMesh;
            
            if (mesh.cardData && mesh !== hoveredMesh) {
              // Unhover previous
              if (hoveredMesh) {
                this.unhoverCard(hoveredMesh);
              }

              // Hover new
              hoveredMesh = mesh;
              this.hoverCard(mesh);
            }
          } else {
            // Unhover all
            if (hoveredMesh) {
              this.unhoverCard(hoveredMesh);
              hoveredMesh = null;
            }
          }
          break;

        case PointerEventTypes.POINTERDOWN:
          const clickResult = this.scene.pick(
            this.scene.pointerX,
            this.scene.pointerY
          );

          if (clickResult?.hit && clickResult.pickedMesh) {
            const mesh = clickResult.pickedMesh as CardMesh;
            if (mesh.cardData) {
              const currentTime = Date.now();
              
              
              if (isMobileDevice) {
                // Mobile: require double-tap
                
                if (lastTapMeshMobile === mesh && currentTime - lastTapTimeMobile < DOUBLE_TAP_DELAY_MOBILE) {
                  const isBack = (clickResult.subMeshId === 1);
                  const shown = isBack ? (mesh.cardBackData || mesh.cardData) : mesh.cardData;
                  this.onCardClick(mesh.cardData, shown);
                  lastTapTimeMobile = 0;
                  lastTapMeshMobile = null;
                } else {
                  lastTapTimeMobile = currentTime;
                  lastTapMeshMobile = mesh;
                }
              } else {
                // Desktop: require double-click (same card) to open
                const delta = currentTime - lastClickTimeDesktop;

                if (lastClickMeshDesktop === mesh && delta < DOUBLE_CLICK_DELAY_DESKTOP) {
                  const isBack = (clickResult.subMeshId === 1);
                  const shown = isBack ? (mesh.cardBackData || mesh.cardData) : mesh.cardData;
                  this.onCardClick(mesh.cardData, shown);
                  lastClickTimeDesktop = 0;
                  lastClickMeshDesktop = null;
                } else {
                  lastClickTimeDesktop = currentTime;
                  lastClickMeshDesktop = mesh;
                }
              }
            }
          }
          break;
      }
    });
  }

  private hoverCard(mesh: CardMesh): void {
    if (mesh.isHovered) return;
    mesh.isHovered = true;

    // Scale up slightly
    const targetScale = new Vector3(1.18, 1.18, 1.18);
    Animation.CreateAndStartAnimation(
      'scaleUp',
      mesh,
      'scaling',
      30,
      10,
      mesh.scaling,
      targetScale,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Move a bit toward the camera (works even if user orbits around)
    const dirToCam = this.camera.position.subtract(mesh.position).normalize();
    const targetPos = mesh.originalPosition!.add(dirToCam.scale(0.55));
    Animation.CreateAndStartAnimation(
      'pullForward',
      mesh,
      'position',
      30,
      10,
      mesh.position,
      targetPos,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Brighten (front/back)
    if (mesh.material instanceof MultiMaterial) {
      const mm = mesh.material as MultiMaterial;
      mm.subMaterials.forEach((sm) => {
        if (sm instanceof StandardMaterial) sm.emissiveColor = new Color3(0.35, 0.35, 0.35);
      });
    }

    document.body.style.cursor = 'pointer';
  }

private unhoverCard(mesh: CardMesh): void {
    if (!mesh.isHovered) return;
    mesh.isHovered = false;

    // Restore scale
    Animation.CreateAndStartAnimation(
      'scaleDown',
      mesh,
      'scaling',
      30,
      10,
      mesh.scaling,
      mesh.originalScale!,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Restore position
    Animation.CreateAndStartAnimation(
      'dropBack',
      mesh,
      'position',
      30,
      10,
      mesh.position,
      mesh.originalPosition!,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Restore emissive
    if (mesh.material instanceof MultiMaterial) {
      const mm = mesh.material as MultiMaterial;
      mm.subMaterials.forEach((sm) => {
        if (sm instanceof StandardMaterial) sm.emissiveColor = new Color3(0.18, 0.18, 0.18);
      });
    }

    document.body.style.cursor = 'default';
  }

  private onCardClick(base: Card, shown: Card): void {
    
    // N'ajoute PAS automatiquement la carte - on utilise le bouton dans le modal

    // Always show modal for the clicked face
    if (this.uiManager) {
      this.uiManager.showCardModal(base, shown);
    } else {
      console.warn('[CardGrid] uiManager is null!');
    }
  }

  private setupFilters(): void {
    // Search input
    const searchInput = document.getElementById('searchInput') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value.toLowerCase();
      this.applyFilters();
    });

    // Filter chips
    document.querySelectorAll('.chip').forEach(chip => {
      chip.addEventListener('click', () => {
        document.querySelectorAll('.chip').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        
        this.rarityFilter = (chip as HTMLElement).dataset.filter || 'all';
        this.applyFilters();
      });
    });
  }

  private applyFilters(): void {
    this.filteredCards = this.cards.filter(card => {
      // Rarity filter
      if (this.rarityFilter !== 'all' && card.rarity !== this.rarityFilter) {
        return false;
      }

      // Search filter
      if (this.searchQuery) {
        const searchable = [
          card.name,
          card.text,
          String(card.number).padStart(3, '0'),
          card.effectId,
          card.type
        ].join(' ').toLowerCase();

        if (!searchable.includes(this.searchQuery)) {
          return false;
        }
      }

      return true;
    }).map(card => ({ ...card }));

    this.createCardGrid();
  }

  dispose(): void {
    this.engine.dispose();
  }
}
